import { userNotificationsQuery } from 'stores/useUserStore.queries';
import { create } from 'zustand';

import type { ImpersonatingUser, Notifications } from 'contexts/UserContext';
import type { FeatureFlagSet } from 'hooks/useFeatureFlags';
import type { RequiredPermissions } from 'types/Permissions';

import LoggingService from 'components/core/logging/LoggingService';
import { impersonationManager } from 'utils/impersonationUtils';
import { hasMinimumScope, hasWhiteLabelScopedAccess, LevelPriority } from 'utils/permissionUtils';

import { logApiError } from 'store/api/graph/interfaces/apiErrors';
import { client } from 'store/apollo/ApolloClient';
import type { UserLoginQuery, UserScope } from 'store/client/graphql';

export type CurrentUser = UserLoginQuery['user'] & { metadata: UserLoginQuery['metadata']['mutation']['user'] };

/**
 * The user store's static action/helper bundle — a stable reference, so subscribing to it (via
 * `useUserActions`) never triggers a re-render. Holds every mutator plus the two stable permission
 * helpers (which take arguments, so they cannot be pure selectors).
 */
export type UserActions = {
  /**
   * Clear the impersonation error.
   */
  clearImpersonationError: () => void;
  /**
   * Check if the current user has at least the given scope level.
   * Stable function — reads the latest `user` via `get()`. Non-reactive: it does NOT re-render on
   * `user.scope` change. For render-time gating that must react to scope changes, use the
   * reactive {@link useHasMinimumScope} hook instead.
   */
  hasMinimumScope: (scope: UserScope) => boolean;
  /**
   * Check if the current user holds all requested permissions.
   * Stable function — reads the latest `user` via `get()`. Non-reactive: it does NOT re-render on
   * `user.permissions` change. For render-time gating that must react to permission changes, use the
   * reactive {@link useHasPermissions} hook instead.
   */
  hasPermissions: (permissions: RequiredPermissions) => boolean;
  /**
   * Start (with a user) or end (with `undefined`) user impersonation, then run the injected redirect.
   */
  impersonateAsUser: (user: ImpersonatingUser | undefined) => void;
  /**
   * Inject the LaunchDarkly feature flags — called by `useInitializeUserStore`.
   */
  setFeatureFlags: (featureFlags: FeatureFlagSet) => void;
  /**
   * Record the last login timestamp.
   */
  setLastLogin: (lastLogin?: number) => void;
  /**
   * Inject the router redirect callback used after impersonation — called by `useInitializeUserStore`.
   */
  setRedirectFn: (redirectFn: (() => void) | undefined) => void;
  /**
   * Set the current user and seed notification badges (LD identify lives in `useInitializeUserStore`).
   */
  setUser: (user: CurrentUser) => void;
  /**
   * Update notification badge state.
   */
  setUserNotifications: (notifications: Notifications) => void;
  /**
   * Fetch the current notification status from the API; resolves `true` on success.
   */
  updateUserNotifications: () => Promise<boolean>;
};

interface UserStoreState {
  /**
   * Router redirect callback — injected by `useInitializeUserStore` via `setRedirectFn`.
   */
  _redirectFn: (() => void) | undefined;
  /**
   * Static action/helper bundle; consumed via `useUserActions`/`useUser`, never off the raw store.
   */
  actions: UserActions;
  /**
   * LaunchDarkly feature flags — injected by `useInitializeUserStore` via `setFeatureFlags`.
   */
  featureFlags: FeatureFlagSet;
  /**
   * Error message from a failed impersonation attempt.
   */
  impersonationError: string | undefined;
  /**
   * Unix timestamp of last login.
   */
  lastLogin: number | undefined;
  /**
   * Current authenticated user.
   */
  user: CurrentUser;
  /**
   * Notification badges state.
   */
  userNotifications: Notifications;
}

const checkPermissions = (userPermissions: CurrentUser['permissions'], required: RequiredPermissions) =>
  !required ||
  required.every(
    ({ level: requiredLevel, resource: requiredResource }) =>
      !!userPermissions?.find(
        ({ level: userLevel, resource: userResource }) =>
          userResource === requiredResource && LevelPriority[userLevel] >= LevelPriority[requiredLevel]
      )
  );

const useUserStore = create<UserStoreState>((set, get) => ({
  _redirectFn: undefined,
  actions: {
    clearImpersonationError: () => set({ impersonationError: undefined }),

    hasMinimumScope: scope => hasMinimumScope(get().user.scope, scope),

    hasPermissions: permissions => checkPermissions(get().user?.permissions, permissions),

    impersonateAsUser: userToImpersonate => {
      if (userToImpersonate) {
        set({ impersonationError: undefined });
        void impersonationManager.tryLoginAs(userToImpersonate.id, get().user).then(result => {
          if (result.success) {
            const redirectFn = get()._redirectFn;
            if (!redirectFn) {
              LoggingService.warn({
                message: '[useUserStore] impersonateAsUser: _redirectFn not set — rolling back impersonation.',
                messageContext: { impersonatorId: get().user?.id, targetUserId: userToImpersonate.id },
              });
              impersonationManager.endImpersonation();
              set({ impersonationError: 'Redirect unavailable — please try again.' });

              return;
            }
            redirectFn();
          } else {
            set({ impersonationError: result.errorMessage });
          }
        });
      } else {
        impersonationManager.endImpersonation();
        if (!get()._redirectFn) {
          LoggingService.warn({
            message:
              '[useUserStore] impersonateAsUser: _redirectFn is not set. Impersonation storage was cleared but no redirect will occur.',
            messageContext: { impersonatorId: get().user?.id },
          });
        }
        get()._redirectFn?.();
      }
    },

    setFeatureFlags: featureFlags => set({ featureFlags }),
    setLastLogin: lastLogin => set({ lastLogin }),
    setRedirectFn: redirectFn => set({ _redirectFn: redirectFn }),

    setUser: user => {
      set({
        user,
        userNotifications: {
          hasIncomingLeadActivities: user.hasIncomingLeadActivities,
          hasUnreadConversations: user.hasUnreadConversations,
        },
      });
    },

    setUserNotifications: userNotifications => set({ userNotifications }),

    updateUserNotifications: async () => {
      try {
        const { data } = await client.query({ fetchPolicy: 'no-cache', query: userNotificationsQuery });
        const userData = data?.user;
        if (!userData) {
          return false;
        }
        const { hasIncomingLeadActivities, hasUnreadConversations } = userData;
        set({ userNotifications: { hasIncomingLeadActivities, hasUnreadConversations } });

        return true;
      } catch (error) {
        logApiError(error, 'Unable to update user notifications status');

        return false;
      }
    },
  },
  featureFlags: {} as FeatureFlagSet,
  impersonationError: undefined,
  lastLogin: undefined,
  user: {} as CurrentUser,

  userNotifications: { hasIncomingLeadActivities: false, hasUnreadConversations: false },
}));

export const useCurrentUser = () => useUserStore(s => s.user);

export const useUserNotifications = () => useUserStore(s => s.userNotifications);

/**
 * Unix timestamp of last login.
 */
export const useLastLogin = () => useUserStore(s => s.lastLogin);

export const useImpersonationError = () => useUserStore(s => s.impersonationError);

export const useUserFeatureFlags = () => useUserStore(s => s.featureFlags);

/**
 * Whether the user can access more than one rooftop (`null` rooftops = unrestricted/all).
 */
export const useCanAccessMultipleRooftops = () =>
  useUserStore(s => s.user?.rooftops === null || (s.user?.rooftops?.length ?? 0) > 1);

export const useIsWhiteLabelScoped = () => useUserStore(s => hasWhiteLabelScopedAccess(s.user.scope));

/**
 * Reactive: whether the current user holds all `required` permissions (`true` when null/empty).
 */
export const useHasPermissions = (required: RequiredPermissions) =>
  useUserStore(s => checkPermissions(s.user?.permissions, required));

/**
 * Reactive: whether the current user meets the given minimum scope (`false` on the bootstrap user).
 */
export const useHasMinimumScope = (minimumScope: UserScope) =>
  useUserStore(s => hasMinimumScope(s.user?.scope, minimumScope));

/**
 * The user store's stable action/helper bundle. Stable reference — never re-renders on its own.
 */
export const useUserActions = () => useUserStore(s => s.actions);

/*
 * TODO: (ED-19201) Hot consumers should call the atomic hooks above directly for true selector
 * isolation. Current useUser() callers subscribe to ALL slices and re-render when any slice changes
 * (same as the previous React Context).
 */
export function useUser() {
  const user = useCurrentUser();
  const userNotifications = useUserNotifications();
  const lastLogin = useLastLogin();
  const impersonationError = useImpersonationError();
  const featureFlags = useUserFeatureFlags();
  const canAccessMultipleRooftops = useCanAccessMultipleRooftops();
  const isWhiteLabelScoped = useIsWhiteLabelScoped();
  const actions = useUserActions();

  return {
    canAccessMultipleRooftops,
    clearImpersonationError: actions.clearImpersonationError,
    featureFlags,
    hasMinimumScope: actions.hasMinimumScope,
    hasPermissions: actions.hasPermissions,
    impersonateAsUser: actions.impersonateAsUser,
    impersonationError,
    isWhiteLabelScoped,
    lastLogin,
    setLastLogin: actions.setLastLogin,
    setUser: actions.setUser,
    setUserNotifications: actions.setUserNotifications,
    updateUserNotifications: actions.updateUserNotifications,
    user,
    userNotifications,
  };
}

const _INITIAL_STATE = useUserStore.getState();

/**
 * Resets the Zustand user store to its initial empty state, including all action references.
 * Call this in test `beforeEach` (or a test/story wrapper) to prevent state leaking between tests.
 */
export const resetUserStore = () => useUserStore.setState(_INITIAL_STATE, true);

type UserStoreSeed = Partial<Omit<UserStoreState, 'actions'>> & {
  /**
   * Action overrides (e.g. `jest.fn()`s) merged onto the live bundle; omitted actions are preserved.
   */
  actions?: Partial<UserActions>;
};

/**
 * Seeds the user store for tests and Storybook. Data fields shallow-merge; the `actions` partial is
 * merged onto the existing bundle, so callers can override only the actions they care about (e.g. a
 * `jest.fn()` `setUser`) without dropping the rest.
 *
 * @param partial - Partial store state to merge in. `partial.actions` is merged into the live bundle.
 */
export const seedUserStore = (partial: UserStoreSeed) =>
  useUserStore.setState(state => ({
    ...partial,
    actions: partial.actions ? { ...state.actions, ...partial.actions } : state.actions,
  }));
